<template>
	<Trap v-if="isShow" :disabled="!isShow">
		<div
			class="overlay"
			role="dialog"
			aria-modal="true"
			tabindex="-1"
			@keydown.esc="close"
		>
			<div class="popup">
				<div class="title-area">
					<h3 class="title">權限異常</h3>
					<button class="close" @click="close">&times;</button>
				</div>
				<hr />
				<div class="content">
					<div class="msgCtnr">
						<div class="preWrap">
							您目前帳號的權限無法存取此頁面，請使用有權之帳號登入
						</div>
					</div>
				</div>
				<div class="btnn-group is-justify-content-end">
					<div class="layer-2">
						<button class="btn btn-primary" @click="ok">確定</button>
						<button class="btn btn-outline-dark" @click="report">回報</button>
					</div>
				</div>
				<div class="statusCtnr">
					<font-awesome-icon class="status-icon" icon="times-circle" />
				</div>
			</div>
		</div>
	</Trap>
</template>

<script>
import { mapState } from "vuex";
import Trap from "vue-focus-lock";
import { checkItemPermission } from "@/utils/auth";
import { popup } from "./base";
import FontAwesomeIcon from "@/utils/fortawesomeSubset.js";

const AUTH_LOG_KEY = "auth-log";
const AUTH_LOG_LIMIT = 20;
const ROLE_KEYS = [
	"is_editor",
	"is_guest",
	"auth_editor",
	"auth_guest",
	"is_manager",
	"is_supermanager",
	"is_superuser"
];

export default {
	mixins: [popup],
	components: {
		Trap,
		FontAwesomeIcon
	},
	created() {
		this.$root.$auth = this;
		window.addEventListener("auth", () => {
			this.isShow = true;
		});
		this.timer = setInterval(this.checkPermission, 10000);
	},
	beforeDestroy() {
		clearInterval(this.timer);
	},
	data() {
		return {
			percent: 0,
			isLoadingUser: false,
			isChecking: false,
			// 最後一次跳出彈窗的網址，只存在記憶體，重新載入頁面或關閉瀏覽器後重設
			lastShownHref: null
		};
	},
	computed: {
		...mapState("user", ["user"])
	},
	watch: {
		// user 晚一步載入完成、權限其實足夠時，自動關掉已顯示的彈窗
		user() {
			if (this.isShow && this.hasPermission()) {
				this.ok();
			}
		}
	},
	methods: {
		hasPermission() {
			const permission = this.$route.matched[this.$route.matched.length - 1]
				.meta.permission;
			return !permission || checkItemPermission(this.user, permission);
		},
		async checkPermission() {
			// 彈窗顯示中或上一輪檢查還沒結束就略過，避免重複 open
			if (this.isShow || this.isChecking) {
				return;
			}
			this.isChecking = true;
			try {
				const isUserReady = await this.ensureUserLoaded();
				if (!isUserReady || this.hasPermission()) {
					return;
				}
				// 與最後一次跳出彈窗的網址相同就不再顯示
				if (this.lastShownHref === window.location.href) {
					return;
				}
				this.lastShownHref = window.location.href;
				this.addAuthLog();
				const action = await this.open();
				if (action === "ok" && !this.hasPermission()) {
					this.$router.push({ name: "login" }).catch(() => {});
				}
			} finally {
				this.isChecking = false;
			}
		},
		readAuthLog() {
			try {
				return JSON.parse(localStorage.getItem(AUTH_LOG_KEY)) || [];
			} catch (error) {
				return [];
			}
		},
		addAuthLog() {
			const route = this.$route.matched[this.$route.matched.length - 1];
			const accessInfo = this.$store.getters["token/accessInfo"];
			const refreshInfo = this.$store.getters["token/refreshInfo"];
			const token = this.$store.state.token.token;
			const record = {
				time: new Date().toISOString(),
				href: window.location.href,
				routeName: this.$route.name,
				permission: route.meta.permission,
				hasAccessToken: !!token.access,
				hasRefreshToken: !!token.refresh,
				accessUserId: accessInfo ? accessInfo.user_id : null,
				accessExp: accessInfo ? accessInfo.exp : null,
				refreshExp: refreshInfo ? refreshInfo.exp : null,
				accessObtainTime: token.accessObtainTime,
				refreshObtainTime: token.refreshObtainTime,
				userId: this.user.id || null,
				userRoles: ROLE_KEYS.filter(k => this.user[k]),
				userAgent: window.navigator.userAgent
			};
			const logs = [...this.readAuthLog(), record].slice(-AUTH_LOG_LIMIT);
			try {
				localStorage.setItem(AUTH_LOG_KEY, JSON.stringify(logs));
			} catch (error) {
				console.log(JSON.stringify(error));
			}
		},
		report() {
			const data = JSON.stringify(this.readAuthLog(), null, 2);
			const blob = new Blob([data], { type: "application/json" });
			const href = URL.createObjectURL(blob);
			const link = document.createElement("a");
			document.body.appendChild(link);
			link.href = href;
			link.download = "authLog.json";
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(href);
			this.isShow = false;
			this.$emit("report");
		},
		async ensureUserLoaded() {
			let accessInfo = this.$store.getters["token/accessInfo"];
			if (!accessInfo) {
				this.$store.commit("token/load");
				accessInfo = this.$store.getters["token/accessInfo"];
			}
			if (!accessInfo) {
				const hasRefreshToken = !!(
					this.$store.state.token &&
					this.$store.state.token.token &&
					this.$store.state.token.token.refresh
				);
				if (hasRefreshToken) {
					try {
						await this.$store.dispatch("token/refresh");
						accessInfo = this.$store.getters["token/accessInfo"];
					} catch (error) {}
				}
			}
			const userId = accessInfo && accessInfo.user_id;
			if (!userId || this.user.id) {
				return true;
			}
			if (this.isLoadingUser) {
				return false;
			}
			this.isLoadingUser = true;
			try {
				await this.$store.dispatch("user/read", userId);
			} catch (error) {
				return false;
			} finally {
				this.isLoadingUser = false;
			}
			return !!this.user.id;
		},
		open() {
			this.isShow = true;
			// 回傳使用者的選擇：ok（確定）、report（回報）、cancel（× 或 Esc）
			this.promise = new Promise(resolve => {
				const events = ["ok", "report", "cancel"];
				const handlers = events.map(event => {
					const handler = () => {
						events.forEach((e, i) => this.$off(e, handlers[i]));
						resolve(event);
					};
					this.$on(event, handler);
					return handler;
				});
			});
			return this.promise;
		}
	}
};
</script>

<style scoped lang="scss">
@import "./style/common.scss";

.overlay {
	position: fixed;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	background: rgba(0, 0, 0, 0.7);
	transition: opacity 500ms;
	opacity: 0;

	visibility: visible;
	opacity: 1;
	z-index: 999;
}
</style>
